import { React, useState, useEffect, useMemo } from "react";
import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import useAxiosPrivate from "../hooks/useAxiosPrivate";
import Add from "@mui/icons-material/Add";
import Button from "@mui/joy/Button";
import ConfirmDialog from "./ConfirmDialog";
import Notification from "./Notification";
import AddCar from "../forms/AddCar";
import AddPopup from "../popups/AddPopup";
import RDataGrid from "./reusables/RDataGrid";
import { GridActionsCellItem } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { LightTooltip } from "../tooltips/ToolTip";
import PageLoader from "./reusables/PageLoader";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CarDrivers from "./CarDrivers";

const Cars = () => {
  const [isFetchingCars, setIsFetchingCars] = useState(true);
  const [cars, setCars] = useState([]);
  const CARS_URL = "api/v1/cars";
  const DELETE_URL = "api/v1/cars/";
  const axiosPrivate = useAxiosPrivate();
  const [openPopup, setOpenPopup] = useState(false);
  const [refreshCars, setSetRefreshCars] = useState(null);

  const [openCarDriversPopup, setOpenCarDriversPopup] = useState(false);
  const [carId, setCarId] = useState(0);

  // Edit car
  const [editCar, setEditCar] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [notify, setNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
  });

  const divStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  };

  const onDelete = async (id) => {
    const controller = new AbortController();
    setConfirmDialog({ ...confirmDialog, isOpen: false });

    try {
      const response = await axiosPrivate.delete(DELETE_URL + id, {
        signal: controller.signal,
      });
      var message = response.data.message;
      setNotify({
        isOpen: true,
        message: message,
        type: "success",
      });
      setSetRefreshCars(new Date());
    } catch (error) {
      var errorMessage = "";
      if (error.response) {
        // The client was given an error response (5xx, 4xx)
        const errorData = error.response.data;
        errorMessage = errorData.message;
      } else if (error.request) {
        // The client never received a response, and the request was never left
        errorMessage = "No server response";
      } else {
        // Anything else
        errorMessage = "Unexpected error occurred";
      }
      setConfirmDialog({
        isOpen: true,
        title: "Failed To Delete Car",
        subTitle: errorMessage,
        action: "Retry",
        onConfirm: () => {
          onDelete(id);
        },
      });
    }
  };

  useEffect(() => {
    if (!isFetchingCars) {
      setIsFetchingCars(true);
    }

    let isMounted = true;
    const controller = new AbortController();

    const getCars = async () => {
      try {
        const response = await axiosPrivate.get(CARS_URL, {
          signal: controller.signal,
        });
        isMounted && setCars(response.data.data);
        setIsFetchingCars(false);
      } catch (error) {
        var errorMessage = "";
        if (error.response) {
          // The client was given an error response (5xx, 4xx)
          const errorData = error.response.data;
          errorMessage = errorData.message;
        } else if (error.request) {
          // The client never received a response, and the request was never left
          errorMessage = "No server response";
        } else {
          // Anything else
          errorMessage = "Unexpected error occurred";
        }
        console.log(errorMessage);
        setIsFetchingCars(false);
      }
    };

    getCars();

    return () => {
      isMounted = false;
      controller.abort();
    };

    /* eslint-disable react-hooks/exhaustive-deps */
  }, [refreshCars]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "Id", width: 60 },
      {
        field: "name",
        headerName: "Name",
        width: 150,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "regNo",
        headerName: "Reg No",
        width: 200,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "sittingCapacity",
        headerName: "Sitting Capacity",
        width: 230,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        width: 100,
        sortable: false,
        disableClickEventBubbling: true,
        cellClassName: "actions",
        getActions: (params) => {
          return [
            <LightTooltip title="Edit car">
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                className="textPrimary"
                onClick={function () {
                  const currentRow = params.row;
                  const selectedCar = JSON.stringify(currentRow, null, 4);
                  setEditCar(true);
                  setSelectedCar(selectedCar);
                  return setOpenPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
            <LightTooltip title="Delete car">
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                color="error"
                onClick={function () {
                  setConfirmDialog({
                    isOpen: true,
                    title: "Are you sure you want to delete this car?",
                    subTitle: "You can't undo this operation",
                    action: "Delete",
                    onConfirm: () => {
                      onDelete(params.row.id);
                    },
                  });
                }}
              />
            </LightTooltip>,
            <LightTooltip title="View Drivers">
              <GridActionsCellItem
                icon={<FormatListBulletedIcon />}
                label="View Drivers"
                className="textPrimary"
                onClick={function () {
                  const id = params.row.id;
                  console.log("id -> " + id);
                  setCarId(id);
                  console.log("carId -> " + carId);
                  return setOpenCarDriversPopup(true);
                }}
                color="success"
              />
            </LightTooltip>,
          ];
        },
      },
    ],
    []
  );

  // Close dialog upon successful day-time add/update
  const closeDialog = (message) => {
    setOpenPopup(false);
    setNotify({
      isOpen: true,
      message: message,
      type: "success",
    });
    setSetRefreshCars(new Date());
  };

  return isFetchingCars ? (
    <PageLoader title="Loading cars..." />
  ) : (
    <Box
      sx={{
        height: "100%",
        width: "100%",
      }}
    >
      <Box height={40} />

      {cars?.length ? (
        <Box
          sx={{
            height: 560,
            width: "100%",
          }}
        >
          <div style={divStyle}>
            <Typography
              variant="h4"
              component="h4"
              sx={{ textAlign: "left", mt: 3, mb: 3 }}
            >
              Manage Cars
            </Typography>
            <Button
              size="lg"
              variant="solid"
              color="primary"
              onClick={function () {
                setEditCar(false);
                setSelectedCar(null);
                setOpenPopup(true);
              }}
              startDecorator={<Add />}
            >
              Add Car
            </Button>
          </div>
          <Box sx={{ width: 1218, margin: "0 auto 16px", height: 560 }}>
            <RDataGrid columns={columns} rows={cars} rowHeight={63} />
          </Box>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={editCar ? "Edit Car" : "Add Car"}
          >
            <AddCar
              closeDialog={closeDialog}
              editCar={editCar}
              selectedCar={selectedCar}
            />
          </AddPopup>
          <AddPopup
            width="md"
            openPopup={openCarDriversPopup}
            setOpenPopup={setOpenCarDriversPopup}
            title="Drivers Assigned To The Car"
          >
            <CarDrivers carId={carId} />
          </AddPopup>
        </Box>
      ) : (
        <div style={divStyle}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ textAlign: "center", mt: 3, mb: 3 }}
          >
            No cars to display
          </Typography>
          <Button
            size="lg"
            variant="solid"
            color="primary"
            onClick={function () {
              setEditCar(false);
              setSelectedCar(null);
              setOpenPopup(true);
            }}
            startDecorator={<Add />}
          >
            Add Car
          </Button>
          <Notification notify={notify} setNotify={setNotify} />
          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <AddPopup
            width="md"
            openPopup={openPopup}
            setOpenPopup={setOpenPopup}
            title={editCar ? "Edit Car" : "Add Car"}
          >
            <AddCar
              closeDialog={closeDialog}
              editCar={editCar}
              selectedCar={selectedCar}
            />
          </AddPopup>
          <AddPopup
            width="md"
            openPopup={openCarDriversPopup}
            setOpenPopup={setOpenCarDriversPopup}
            title="Drivers Assigned To The Car"
          >
            <CarDrivers carId={carId} />
          </AddPopup>
        </div>
      )}
    </Box>
  );
};

export default Cars;
